import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import {
    Redirect,
    Route,
    Switch,
    useLocation,
    useRouteMatch,
} from 'react-router-dom';
import { BasePage, PageType } from 'src/components/BasePage';
import { RoutePaths } from 'src/core/router/RoutePaths';
import { ScoringModelDefaultRedirect } from 'src/pages/SettingsPage/components/ScoringModelDefaultRedirect';
import { ScoringModelRedirect } from 'src/pages/SettingsPage/components/ScoringModelRedirect';
import { VersionCreate } from 'src/pages/SettingsPage/components/VersionCreate';
import { SettingsScoringModelStore } from 'src/stores/SettingsStores/SettingsScoringModelStore/SettingsScoringModelStore';

const entityName = 'scoring-model';
const store = SettingsScoringModelStore;

export const SettingsScoringModelPageDetails = observer(() => {
    const { path } = useRouteMatch();
    const { pathname } = useLocation();

    // Memoize the scoring model ID to prevent recalculation on every render
    const idOfScoreModel = useMemo(
        () => store?.listLoader?.data?.[0]?.id,
        [store?.listLoader?.data],
    );

    useEffect(() => {
        store.loadList();

        return () => {
            store.listLoader.reset();
        };
    }, []);

    useEffect(() => {
        // Only load versions if we have a valid model ID
        if (idOfScoreModel) {
            store.loadVersionsList(idOfScoreModel);
        }

        return () => {
            store?.listOfModelVersions?.reset();
        };
    }, [idOfScoreModel]);

    return (
        <BasePage
            pageName={entityName}
            type={PageType.FULL}
            isLoading={
                store?.listOfModelVersions?.isLoading ||
                store?.listLoader?.isLoading
            }
            data-testid="settings-scoring-model"
        >
            <Switch>
                <Route exact path={RoutePaths.settingScoringModelDetails}>
                    <ScoringModelRedirect basePath={path} />
                </Route>

                <Route
                    exact
                    path={RoutePaths.settingScoringModelVersionDetails}
                >
                    <VersionCreate store={store} />
                </Route>

                {!pathname.match(/\d/) && Boolean(store?.list?.length > 0) && (
                    <Route exact path={`${path}`}>
                        <ScoringModelDefaultRedirect basePath={path} />
                    </Route>
                )}

                <Route exact path={`${path}/*`}>
                    <Redirect to={path} />
                </Route>
            </Switch>
        </BasePage>
    );
});
